import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { getFiGrowthBook } from '../../lib/growthbook';
import { DEV_MODE, getCookieDomain, removeCookie } from '../../lib/util';
import {
  getNanoVariantPetGenderSelect,
  getNanoVariantPetName,
  hasNanoVariantPetName,
} from '../../lib/util/petUrlParams';
import styles from './RescueBanner.module.scss';

export const RESCUE_BANNER_COOKIE = 'rescue_banner_data';
export const RESCUE_BANNER_DISMISSED_COOKIE = 'rescue_banner_dismissed';
export const RESCUE_BANNER_COOKIE_EXPIRATION_DAYS = 7;
export const RESCUE_GWA_PDP_FLAG = 'ecom-rescue-gwa-pdp';
export const RESCUE_PDP_REDESIGN_FLAG = 'ecom-rescue-pdp-redesign';

interface RescueBannerData {
  orgName: string | null;
  offer: string;
}

/**
 * Kill switch for the whole adoption programme. Every rescue surface - banner, offer box, chip
 * module, testimonials, faq, the suppressed promo field, the cart's renewal and discount rows -
 * reads `showRescueGwaVariant()`, so one value moves all of them together rather than leaving a
 * half-rescue page.
 *
 * Defaults to on: the flag exists to switch a fully-rolled-out feature off in an incident, so a
 * GrowthBook fetch that times out (3s, see `growthbook.ts`) must not take the adoption offer down
 * with it. The consequence is that flipping it off does not reach a shopper whose features failed
 * to load - acceptable for a rare client-side failure, but it does mean this is not a security
 * boundary. Entry is still governed by the signed entitlement, which is server-side.
 *
 * **Do not put an experiment rule on this flag** - use `RESCUE_PDP_REDESIGN_FLAG` below. This one is
 * not a design toggle. `RescueLanding` reads it before seeding any rescue state, so `false` means no
 * `gwa_entitlement`, no coupon cookie and no `rescue_banner_data` - an adopter in that arm is quoted
 * full price for the collar and membership their shelter told them was free. That is the intended
 * behaviour for killing the programme and an unusable control arm for a design test.
 */
export function rescueGwaPdpEnabled(): boolean {
  return getFiGrowthBook().getFeatureValue<boolean>(RESCUE_GWA_PDP_FLAG, true);
}

/**
 * Is this shopper an adopter, with the programme switched on - i.e. is the gift real for them.
 *
 * Cookie first, deliberately. The flag is read on every Mini PDP, cart and subscription-picker
 * render, so evaluating it ahead of the cookie would bucket - and fire an exposure for - the entire
 * shop population while only rescue arrivals could possibly see the treatment. Short-circuiting on
 * the cookie keeps any experiment population equal to the eligible population.
 */
export function showRescueGwaVariant(): boolean {
  return !!Cookies.get(RESCUE_BANNER_COOKIE) && rescueGwaPdpEnabled();
}

/**
 * Should this adopter see the redesigned adoption page, as opposed to the standard Mini PDP with
 * the adoption discount applied to it. This is the A/B split point, and the only one: the entitlement,
 * the auto-applied coupon, the co-brand banner and the discount on the plan card are all deliberately
 * *outside* it, on `showRescueGwaVariant()`, so both arms are quoted the same price and both are told
 * the gift is real. Only the presentation moves.
 *
 * Reads through the eligibility gate first so a non-adopter never evaluates this flag and never
 * lands in the experiment - see `showRescueGwaVariant()`.
 *
 * Defaults to `false`: this flag carries the 50/50 experiment, so a GrowthBook fetch that times out
 * must put the adopter in control (standard Mini PDP, discount still applied) rather than in an
 * untracked treatment that would contaminate the test.
 *
 * Setting the experiment up in GrowthBook: `hashAttribute` **must** be `anonymousId`. We never
 * register an `id` attribute (see `growthbook.ts` `setAttributes`), so an experiment left on
 * GrowthBook's default hash attribute buckets every shopper on `undefined`, excludes them all and
 * drops them to `defaultValue` - the treatment reaches 0% while looking like a clean rollout.
 * `anonymousId` is the only attribute guaranteed present for a logged-out adopter
 * (`userEncodedUuid` needs a Segment identity, which a cold arrival from a rescue link lacks).
 */
export function showRescuePdpRedesign(): boolean {
  return showRescueGwaVariant() && getFiGrowthBook().getFeatureValue<boolean>(RESCUE_PDP_REDESIGN_FLAG, false);
}

export function rescueBannerDismissed(): boolean {
  return !!Cookies.get(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * records the dismissal in its own cookie and leaves `rescue_banner_data` in place. that cookie is
 * also the rescue-variant gate (`showRescueGwaVariant`), so clearing it here would turn off the
 * whole rescue experience for the session, not just this banner. scoped like the other shop cookies
 * so the dismissal holds across subdomains, and expires with the rescue context it belongs to.
 */
export function dismissRescueBanner() {
  Cookies.set(RESCUE_BANNER_DISMISSED_COOKIE, '1', {
    path: '/',
    expires: RESCUE_BANNER_COOKIE_EXPIRATION_DAYS,
    domain: getCookieDomain(),
    secure: !DEV_MODE,
  });
}

/**
 * `removeCookie` rather than `Cookies.remove`: the dismissal cookie is written against the shared
 * parent domain, and a removal that omits that domain silently does nothing in production.
 */
export function clearRescueBannerDismissal() {
  removeCookie(RESCUE_BANNER_DISMISSED_COOKIE);
}

/**
 * Reads rescue banner data from a cookie set by the /rescue/:slug landing route.
 * Renders the co-brand header block on the shop page: eyebrow naming the rescue, headline stating
 * the offer. Re-checks the cookie on every navigation so it picks up the value after redirect.
 *
 * Deliberately not dismissible: the rescue connection is the whole reason this traffic converts,
 * and dismissal used to read as "turn the rescue experience off". The dismissal cookie helpers
 * above stay exported for the landing route and existing tests, but nothing consults them here.
 */
export default function RescueBanner() {
  const location = useLocation();
  const { t } = useTranslation();
  const [bannerData, setBannerData] = useState<RescueBannerData | null>(null);

  useEffect(() => {
    const raw = Cookies.get(RESCUE_BANNER_COOKIE);
    if (raw) {
      try {
        setBannerData(JSON.parse(raw));
      } catch {
        // ignore malformed cookie
      }
    }
  }, [location.pathname]);

  if (!bannerData) {
    return null;
  }

  // a program with no org name gets the generic partner eyebrow, never "Your rescue". the headline
  // is shared: it states the offer as an attribute of the adoption, so no payer has to be named.
  return (
    <div className={styles.cobrand}>
      <div className={styles.cobrandInner}>
        <div className={styles.eyebrow}>
          {bannerData.orgName
            ? t('rescue_banner_eyebrow', { name: bannerData.orgName })
            : t('rescue_banner_eyebrow_generic')}
        </div>
        <h3 className={styles.headline}>
          {/* personalized only while the adopter link's pet params are in the url; the generic line
              is the fallback everywhere else, including after navigation drops the query string */}
          {hasNanoVariantPetName()
            ? t('rescue_banner_headline_named', {
                petName: getNanoVariantPetName(),
                gender: getNanoVariantPetGenderSelect(),
              })
            : t('rescue_banner_headline')}
        </h3>
      </div>
    </div>
  );
}
